import type { GroveCritter } from "./level";

export type CritterSpriteState = "walk-a" | "walk-b" | "stunned" | "defeated";
type PixelRect = readonly [x: number, y: number, w: number, h: number];

const OUTLINE = "#182d36";
const EYE_WHITE = "#fff8da";

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function shadeHex(color: string, amount: number): string {
  const match = /^#([0-9a-f]{6})$/i.exec(color);
  if (!match) return color;
  const value = Number.parseInt(match[1], 16);
  const channel = (shift: number) => clamp(((value >> shift) & 0xff) + amount, 0, 255);
  return `#${[channel(16), channel(8), channel(0)]
    .map((part) => Math.round(part).toString(16).padStart(2, "0"))
    .join("")}`;
}

function rects(ctx: CanvasRenderingContext2D, color: string, values: readonly PixelRect[]): void {
  ctx.fillStyle = color;
  for (const [x, y, w, h] of values) ctx.fillRect(x, y, w, h);
}

function eyes(
  ctx: CanvasRenderingContext2D,
  state: CritterSpriteState,
  leftX: number,
  rightX: number,
  y: number,
): void {
  if (state === "stunned" || state === "defeated") {
    rects(ctx, OUTLINE, [
      [leftX, y, 1, 1], [leftX + 2, y, 1, 1], [leftX + 1, y + 1, 1, 1],
      [leftX, y + 2, 1, 1], [leftX + 2, y + 2, 1, 1],
      [rightX, y, 1, 1], [rightX + 2, y, 1, 1], [rightX + 1, y + 1, 1, 1],
      [rightX, y + 2, 1, 1], [rightX + 2, y + 2, 1, 1],
    ]);
    return;
  }
  rects(ctx, EYE_WHITE, [[leftX, y, 3, 3], [rightX, y, 3, 3]]);
  rects(ctx, OUTLINE, [[leftX + 1, y + 1, 1, 2], [rightX + 1, y + 1, 1, 2]]);
}

function drawBud(
  ctx: CanvasRenderingContext2D,
  state: CritterSpriteState,
  main: string,
  light: string,
  shade: string,
): void {
  const step = state === "walk-b" ? 1 : 0;
  if (state === "stunned") {
    rects(ctx, OUTLINE, [[-9, -8, 18, 7], [-7, -10, 14, 2], [-8, -1, 5, 1], [3, -1, 5, 1]]);
    rects(ctx, main, [[-7, -7, 14, 5], [-5, -9, 10, 2]]);
    rects(ctx, shade, [[-7, -4, 5, 2]]);
    rects(ctx, "#347d48", [[-5, -12, 4, 3], [-1, -13, 3, 4], [3, -12, 3, 3]]);
    eyes(ctx, state, -5, 2, -7);
    return;
  }
  rects(ctx, OUTLINE, [
    [-7, -15, 14, 2], [-9, -13, 18, 10], [-7, -3, 14, 3],
    [-10, -10, 2, 6], [8, -10, 2, 6],
    [-7 - step, -1, 5, 2], [2 + step, -1, 5, 2],
    [-6, -18, 3, 4], [-2, -19, 4, 5], [3, -18, 3, 4],
  ]);
  rects(ctx, main, [[-7, -12, 14, 9], [-5, -14, 10, 12], [-9, -8, 2, 4], [7, -8, 2, 4]]);
  rects(ctx, light, [[-5, -12, 4, 5], [-7, -6, 3, 2]]);
  rects(ctx, shade, [[3, -12, 4, 8], [-4, -3, 8, 2]]);
  rects(ctx, "#347d48", [[-5, -17, 2, 3], [-1, -18, 3, 4], [4, -17, 2, 3]]);
  eyes(ctx, state, -5, 2, -11);
  rects(ctx, "#5a2830", [[-1, -6, 3, 1], [0, -5, 1, 1]]);
}

function drawMole(
  ctx: CanvasRenderingContext2D,
  state: CritterSpriteState,
  main: string,
  light: string,
  shade: string,
): void {
  const step = state === "walk-b" ? 1 : 0;
  if (state === "stunned") {
    rects(ctx, "#3a2b2b", [[-9, -7, 17, 6], [-7, -9, 12, 2], [8, -5, 3, 3]]);
    rects(ctx, main, [[-7, -6, 14, 4], [-5, -8, 10, 2]]);
    rects(ctx, light, [[5, -5, 4, 2]]);
    eyes(ctx, state, -4, 2, -7);
    return;
  }
  rects(ctx, "#3a2b2b", [
    [-8, -13, 13, 2], [-9, -11, 17, 9], [-7, -2, 14, 2], [7, -9, 4, 5],
    [-10, -7, 3, 4], [8, -6, 3, 3], [-7 - step, -1, 5, 2], [2 + step, -1, 5, 2],
  ]);
  rects(ctx, main, [[-7, -11, 14, 8], [-5, -12, 9, 10], [7, -8, 2, 3]]);
  rects(ctx, light, [[2, -9, 7, 5], [-5, -10, 3, 4]]);
  rects(ctx, shade, [[-7, -5, 5, 3], [1, -3, 5, 2]]);
  rects(ctx, "#d9b287", [[7, -7, 3, 3], [-10, -6, 3, 2]]);
  rects(ctx, OUTLINE, [[9, -7, 2, 2], [-2, -9, 2, 2]]);
  if (state === "defeated") eyes(ctx, state, -4, 1, -10);
  rects(ctx, "#fff4d2", [[4, -4, 2, 2]]);
}

function drawBird(
  ctx: CanvasRenderingContext2D,
  state: CritterSpriteState,
  main: string,
  light: string,
  shade: string,
): void {
  const step = state === "walk-b" ? 1 : 0;
  if (state === "stunned") {
    rects(ctx, "#3b3230", [[-8, -7, 16, 6], [-6, -9, 11, 2], [8, -5, 3, 2]]);
    rects(ctx, main, [[-6, -6, 13, 4], [-4, -8, 8, 2]]);
    rects(ctx, "#f28c2e", [[7, -5, 4, 2]]);
    eyes(ctx, state, -4, 2, -7);
    return;
  }
  rects(ctx, "#3b3230", [
    [-6, -15, 11, 3], [-8, -12, 16, 10], [-6, -2, 12, 2], [7, -12, 4, 4],
    [-8, -10, 3, 6], [-4 - step, -1, 2, 2], [2 + step, -1, 2, 2],
  ]);
  rects(ctx, main, [[-5, -14, 9, 4], [-6, -11, 12, 8], [-7, -8, 2, 4]]);
  rects(ctx, light, [[-4, -13, 4, 4], [-4, -6, 5, 3]]);
  rects(ctx, shade, [[1, -11, 5, 8], [-6, -4, 4, 2]]);
  rects(ctx, "#f28c2e", [[7, -11, 4, 3], [9, -10, 3, 1], [-4 - step, 0, 3, 1], [2 + step, 0, 3, 1]]);
  eyes(ctx, state, -3, 2, -12);
  rects(ctx, "#744235", [[-6, -8, 5, 4], [-5, -7, 3, 2]]);
}

function drawCrab(
  ctx: CanvasRenderingContext2D,
  state: CritterSpriteState,
  main: string,
  light: string,
  shade: string,
): void {
  const step = state === "walk-b" ? 1 : 0;
  if (state === "stunned") {
    rects(ctx, "#57333a", [[-9, -7, 18, 6], [-7, -9, 14, 2], [-11, -5, 3, 2], [8, -5, 3, 2]]);
    rects(ctx, main, [[-7, -6, 14, 4], [-5, -8, 10, 2]]);
    eyes(ctx, state, -5, 2, -7);
    return;
  }
  rects(ctx, "#57333a", [
    [-8, -12, 16, 10], [-6, -14, 4, 3], [2, -14, 4, 3],
    [-11, -12, 3, 4], [8, -12, 3, 4], [-12, -14, 3, 3], [9, -14, 3, 3],
    [-10 - step, -3, 4, 2], [-3 + step, -2, 4, 2], [5 - step, -3, 4, 2],
  ]);
  rects(ctx, main, [[-7, -10, 14, 7], [-5, -11, 10, 9], [-10, -11, 2, 3], [8, -11, 2, 3]]);
  rects(ctx, light, [[-5, -9, 5, 4], [-9, -13, 2, 2]]);
  rects(ctx, shade, [[2, -9, 5, 5], [-6, -4, 12, 2]]);
  eyes(ctx, state, -5, 2, -14);
  rects(ctx, "#7a2c3b", [[-1, -6, 3, 1]]);
}

function drawPuffer(
  ctx: CanvasRenderingContext2D,
  state: CritterSpriteState,
  main: string,
  light: string,
  shade: string,
): void {
  const step = state === "walk-b" ? 1 : 0;
  if (state === "stunned") {
    rects(ctx, "#5a4a34", [[-9, -7, 18, 6], [-7, -9, 14, 2], [-11, -5, 2, 2], [9, -5, 2, 2]]);
    rects(ctx, main, [[-7, -6, 14, 4], [-5, -8, 10, 2]]);
    eyes(ctx, state, -4, 2, -7);
    return;
  }
  rects(ctx, "#5a4a34", [
    [-7, -15, 14, 2], [-9, -13, 18, 11], [-7, -2, 14, 2],
    [-10, -15, 2, 3], [8, -15, 2, 3], [-11, -10, 3, 2], [9, -10, 3, 2],
    [-10, -4, 2, 3], [8, -4, 2, 3], [-5, -17, 2, 3], [3, -17, 2, 3],
    [-5 - step, -1, 4, 2], [1 + step, -1, 4, 2],
  ]);
  rects(ctx, main, [[-7, -13, 14, 10], [-5, -14, 10, 12]]);
  rects(ctx, light, [[-5, -12, 4, 5], [-6, -6, 3, 3]]);
  rects(ctx, shade, [[2, -12, 5, 8], [-3, -3, 7, 2], [-10, -9, 3, 4]]);
  if (state === "defeated") eyes(ctx, state, -4, 1, -11);
  else {
    rects(ctx, EYE_WHITE, [[1, -11, 3, 3]]);
    rects(ctx, OUTLINE, [[2, -10, 1, 2]]);
  }
  rects(ctx, "#7c3f3b", [[7, -7, 3, 2], [9, -6, 2, 1]]);
}

export function drawPixelCritter(
  ctx: CanvasRenderingContext2D,
  critter: GroveCritter,
  state: CritterSpriteState,
): void {
  const species = critter.species ?? "bud";
  const defaults: Record<NonNullable<GroveCritter["species"]>, string> = {
    bud: "#78be4f",
    mole: "#b38a5b",
    bird: "#e5a94f",
    crab: "#ed7162",
    puffer: "#edbf4b",
  };
  const main = critter.color ?? defaults[species];
  const light = shadeHex(main, 36);
  const shade = shadeHex(main, -38);

  if (species === "mole") drawMole(ctx, state, main, light, shade);
  else if (species === "bird") drawBird(ctx, state, main, light, shade);
  else if (species === "crab") drawCrab(ctx, state, main, light, shade);
  else if (species === "puffer") drawPuffer(ctx, state, main, light, shade);
  else drawBud(ctx, state, main, light, shade);
}

export function drawPixelStunStars(
  ctx: CanvasRenderingContext2D,
  visualTime: number,
  reducedMotion: boolean,
): void {
  const orbit = reducedMotion ? 0 : visualTime * 2.5;
  const leftX = Math.round(-20 + Math.cos(orbit) * 5);
  const leftY = Math.round(-46 + Math.sin(orbit) * 4);
  const rightX = Math.round(15 + Math.cos(orbit + Math.PI) * 5);
  const rightY = Math.round(-50 + Math.sin(orbit + Math.PI) * 4);
  ctx.fillStyle = "#8a5b20";
  ctx.fillRect(leftX - 2, leftY - 8, 5, 17);
  ctx.fillRect(leftX - 8, leftY - 2, 17, 5);
  ctx.fillRect(rightX - 2, rightY - 8, 5, 17);
  ctx.fillRect(rightX - 8, rightY - 2, 17, 5);
  ctx.fillStyle = "#ffdf52";
  ctx.fillRect(leftX, leftY - 6, 3, 13);
  ctx.fillRect(leftX - 6, leftY, 13, 3);
  ctx.fillRect(rightX, rightY - 6, 3, 13);
  ctx.fillRect(rightX - 6, rightY, 13, 3);
}
